import React, { useMemo } from 'react';
import { useCrmData } from '../../../contexts/CrmProviders';
import { formatCurrency } from '../../../utils/formatters';
import DashboardCard, { ChartEmpty, MONEY_CLASS, NUM_CLASS } from './DashboardCard';
import { cn } from '@/lib/utils';

interface SalesRankingItem {
    rank: number;
    id: string;
    name: string;
    value: number;
    dealsCount: number;
}

interface SalesRankTableProps {
    ranking: SalesRankingItem[];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SalesRankTable: React.FC<SalesRankTableProps> = ({ ranking }) => {
    const { crmUsers } = useCrmData();
    const rows = useMemo(() => ranking.slice(0, 5), [ranking]);

    const displayName = (agent: SalesRankingItem) => {
        if (!UUID_RE.test(agent.name)) return agent.name;
        const user = crmUsers.find((u) => u.id === agent.name || u.id === agent.id);
        return user ? user.name : 'Não atribuído';
    };

    return (
        <DashboardCard title="Ranking de vendas" flush>
            {rows.length === 0 ? (
                <ChartEmpty>Nenhuma venda neste período.</ChartEmpty>
            ) : (
                <div className="overflow-x-auto">
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th scope="col" className="w-10">#</th>
                                <th scope="col">Vendedor</th>
                                <th scope="col" className="text-right">Neg.</th>
                                <th scope="col" className="text-right">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((agent) => {
                                const name = displayName(agent);
                                return (
                                    <tr key={agent.id}>
                                        <td>
                                            <span className={cn(NUM_CLASS, 'text-sm', agent.rank <= 3 ? 'text-primary' : 'text-muted')}>
                                                #{agent.rank}
                                            </span>
                                        </td>
                                        <td className="max-w-0 truncate font-sans text-sm font-normal text-muted" title={name}>
                                            {name}
                                        </td>
                                        <td className={cn(NUM_CLASS, 'text-right text-sm text-muted')}>{agent.dealsCount}</td>
                                        <td className={cn(MONEY_CLASS, 'whitespace-nowrap text-right text-sm text-primary')}>
                                            {formatCurrency(agent.value)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </DashboardCard>
    );
};

export default SalesRankTable;
